import { styled } from '@mui/material/styles';
import { ListItem, Chip, Box, Typography } from '@mui/material';
import { keyframes } from '@emotion/react';
import { useTheme,alpha } from '@mui/material/styles';

const useNotificationStyles = () => {
  const theme = useTheme();

  const StyledListItem = styled(ListItem)(({ notificationtype, notified, clickable }) => ({
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(2),
    boxShadow: theme.shadows[1],
    transition: 'all 0.3s ease',
    backgroundColor: notified ? theme.palette.background.default : theme.palette.background.paper,
    '&:hover': {
      boxShadow: theme.shadows[3],
      transform: clickable ? 'translateY(-2px)' : 'none',
      cursor: clickable ? 'pointer' : 'default',
    },
    ...(notificationtype === 'system' && {
      borderLeft: `4px solid ${theme.palette.error.main}`,
    }),
    ...(notificationtype === 'observation' && {
      borderLeft: `4px solid ${theme.palette.primary.main}`,
    }),
    ...(notificationtype === 'leak' && {
      borderLeft: `4px solid ${theme.palette.warning.main}`,
    }),
    opacity: clickable ? 1 : 0.7,
  }));

  const StyledChip = styled(Chip)(({ notificationtype }) => ({
    marginRight: theme.spacing(1),
    padding: '4px 8px',
    borderRadius: '16px',
    fontWeight: 600,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
    },
    ...(notificationtype === 'system' && {
      backgroundColor: alpha(theme.palette.error.main, 0.1),
      color: theme.palette.error.main,
      border: `1px solid ${theme.palette.error.main}`,
    }),
    ...(notificationtype === 'observation' && {
      backgroundColor: alpha(theme.palette.primary.main, 0.1),
      color: theme.palette.primary.main,
      border: `1px solid ${theme.palette.primary.main}`,
    }),
    ...(notificationtype === 'leak' && {
      backgroundColor: alpha(theme.palette.warning.main, 0.1),
      color: theme.palette.warning.main,
      border: `1px solid ${theme.palette.warning.main}`,
    }),
  }));

  const NotificationContainer = styled(Box)({
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    width: '100%',
    overflow: 'auto'
  });

  const NotificationHeader = styled(Box)({
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  });

  const NotificationList = styled(Box)({
    flexGrow: 1,
    overflow: 'auto',
    padding: theme.spacing(3),
  });

  const NotificationContent = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    padding: '20px',
    maxWidth: '100%'
  });

  const NotificationTitle = styled(Typography)({
    display: 'flex', 
    alignItems: 'center', 
    color: '#242A45',
  });

  const NotificationMessage = styled(Typography)({
    fontSize: '16px',
    color: theme.palette.text.secondary,
    wordWrap: 'break-word',
    maxWidth:'30%',
    marginRight: '5%'
  });

  const NotificationDate = styled(Typography)({
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    fontSize: '15px',
  });

  const pulse = keyframes`
    0% {
      box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.2);
    }
    70% {
      box-shadow: 0 0 0 10px rgba(0, 0, 0, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
    }
  `;

  const LegendContainer = styled(Box)({
    position: 'sticky',
    top: theme.spacing(2),
    alignSelf: 'flex-start',
    marginTop: '30px',
    marginLeft: 'auto',
    marginRight: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    borderRadius: '20px',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(0, 0, 0, 0.05)',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.7)',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-5px) scale(1.02)',
      boxShadow: '0 15px 40px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.1)',
    },
  });

  const LegendItem = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(1, 1.5),
    borderRadius: '12px',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.05)',
    },
  });

  const LegendColor = styled(Box)(({ color }) => ({
    width: '12px',
    height: '12px',
    backgroundColor: color,
    borderRadius: '50%',
    boxShadow: `0 0 10px ${color}, 0 0 20px ${color}`,
    animation: `${pulse} 2s infinite`,
  }));

  const LegendText = styled(Typography)({
    color: 'rgba(0, 0, 0, 0.8)',
    fontWeight: 500,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    fontSize: '0.75rem',
  });

  const LegendTitle = styled(Typography)({
    color: 'rgba(0, 0, 0, 0.9)',
    fontWeight: 700,
    letterSpacing: '1px',
    textTransform: 'uppercase',
    fontSize: '0.8rem',
    marginBottom: theme.spacing(1),
    textAlign: 'center',
    borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
    paddingBottom: theme.spacing(1),
  });

  return {
    StyledListItem,
    StyledChip,
    NotificationContainer,
    NotificationHeader,
    NotificationList,
    NotificationContent,
    NotificationTitle,
    NotificationMessage,
    NotificationDate,
    LegendContainer,
    LegendItem,
    LegendColor,
    LegendText,
    LegendTitle,
  };
};

export default useNotificationStyles;